import {
  CheckCircleIcon,
  XCircleIcon,
  ArrowDownTrayIcon,
  ClockIcon,
  StopCircleIcon,
} from "@heroicons/react/24/outline";

// ============================================================================
// Download Status Types
// ============================================================================

/**
 * Represents the possible states of a file download operation.
 *
 * - `pending`: Download is queued but not yet started
 * - `downloading`: Download is currently in progress
 * - `completed`: Download finished successfully
 * - `failed`: Download encountered an error
 * - `cancelled`: Download was cancelled by the user
 */
export type DownloadStatus =
  | "pending"
  | "downloading"
  | "completed"
  | "failed"
  | "cancelled";

/**
 * Represents the progress state of a single file download.
 */
export type DownloadProgress = {
  /** The name of the file being downloaded */
  fileName: string;
  /** Current status of the download */
  status: DownloadStatus;
  /** Error message if the download failed */
  error?: string;
};

// ============================================================================
// Download Status UI Helpers
// ============================================================================

/**
 * Returns the appropriate icon component for a given download status.
 *
 * @param status - The current download status
 * @returns A React element with the corresponding status icon
 *
 * @example
 * <div>{getDownloadStatusIcon("completed")}</div>
 * // Renders a green checkmark icon
 */
export const getDownloadStatusIcon = (status: DownloadStatus): JSX.Element => {
  switch (status) {
    case "completed":
      return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
    case "failed":
      return <XCircleIcon className="h-5 w-5 text-red-600" />;
    case "downloading":
      return (
        <ArrowDownTrayIcon className="h-5 w-5 animate-bounce text-indigo-600" />
      );
    case "pending":
      return <ClockIcon className="h-5 w-5 text-gray-400" />;
    case "cancelled":
      return <StopCircleIcon className="h-5 w-5 text-red-500" />;
  }
};

/**
 * Returns a human-readable status text for a given download status.
 *
 * @param status - The current download status
 * @returns A user-friendly description of the status
 *
 * @example
 * getStatusText("downloading");
 * // Returns: "Initiating download..."
 */
export const getDownloadStatusText = (status: DownloadStatus): string => {
  switch (status) {
    case "completed":
      return "Download initiated";
    case "downloading":
      return "Initiating download...";
    case "pending":
      return "Waiting...";
    case "failed":
      return "Failed to initiate";
    case "cancelled":
      return "Cancelled";
  }
};
